import { KeyValue } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpEventType, HttpRequest } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { catchError, last, map, of, Subscription, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorDialogComponent } from '../error-dialog/error-dialog.component';
import { AFVendorBatchRequest, AFVendorBatchRequestDomain, KeyVal } from '../model/af.model';
import { AuthService } from '../service/auth.service';
import { EventService } from '../service/event.service';
import { BeforeSubmitErrorEvent, ShowProgressEvent, UploadEvent } from '../service/events';
import { VendorService } from '../service/vendor.service';

@Component({
    selector: 'app-vendor-batch-request',
    templateUrl: './vendor-batch-request.component.html',
    styleUrls: ['./vendor-batch-request.component.scss'],
    standalone: false
})
export class VendorBatchRequestComponent implements OnInit, OnDestroy {
  supportedFileExtension: string[] = ['xls', 'xlsx'];
  maxFileSupportedInMb = 5;

  /** Link text */
  @Input() text = 'Choose File';

  /** Name used in form which will be sent in HTTP request. */
  @Input() param = 'file';

  /** Target URL for file uploading. */
  @Input() target = environment.api_uri + "submitVendorBatchRequest";

  /** File extension that accepted, same as 'accept' of <input type="file" />.
      By the default, it's set to 'image/*'. */
  @Input() accept = 'image/*';

  /** Allow you to add handler after its completion. Bubble up response text from remote. */
  @Output() complete = new EventEmitter<string>();

  sub: Subscription;
  selected: boolean = true;
  vendorMap: boolean;

  vendorBatchRequestForm: UntypedFormGroup;
  vendorBatchRequestData: AFVendorBatchRequestDomain;
  accountArray: KeyVal[];

  @ViewChild("fileUpload") fileUploadElement: ElementRef;

  files: Array<FileUploadModel> = [];
  vendorBatchForm: UntypedFormGroup;
  accountControl: UntypedFormControl;
  displayedColumns: string[] = ['fileName', 'vendorFirstName', 'fileSize', 'uploadDateString'];
  dataSource = new MatTableDataSource<AFVendorBatchRequest>();

  @ViewChild("vendorBatchElement") vendorBatchElement: ElementRef;
  @ViewChild("addedFile") addedFile: ElementRef;

  constructor(private readonly fb: UntypedFormBuilder, private readonly dialog: MatDialog, private readonly _snackBar: MatSnackBar, private readonly vendorService: VendorService,
    private readonly userService: AuthService, private readonly _http: HttpClient, private readonly eventBus: EventService) { }

  @ViewChild(MatPaginator)
  set paginator(value: MatPaginator) {
    this.dataSource.paginator = value;
  }

  ngOnInit() {
    this.sub = this.eventBus.on(
      UploadEvent,
      (): void => {
        this.uploadFiles();
      }
    );

    this.createGroup();
    this.subscribeEvents();


    this.vendorService.getFocusEmitter().subscribe({
      next: (data: any) => {
        if (data === 'vendorBatchRequest') {
          this.vendorFormFocus();
        }
      },
      error: (error: Error) => {
        console.log(error);
      }
    });
  }

  vendorFormFocus() {
    setTimeout(() => {
      this.vendorBatchElement.nativeElement.focus();
    });
  }

  subscribeEvents() {
    this.vendorService.getVendorBatchRequest().subscribe({
      next: (data: AFVendorBatchRequestDomain) => {
        this.vendorBatchRequestData = data;
        this.vendorBatchRequestForm.patchValue(data);
        this.vendorMap = (Object.entries(this.vendorBatchRequestData.vendorAccountMap).length === 0 &&
          this.vendorBatchRequestData.vendorAccountMap.constructor === Object)

        this.accountArray = [
          { id: this.vendorBatchRequestData.vendorAccountSerialNum, value: this.vendorAccount.value },
        ];

        this.accountControl = new UntypedFormControl(this.accountArray[0].id);

        if (this.vendorBatchRequestData.batchRequestFiles != null) {
          this.dataSource.data = this.vendorBatchRequestData.batchRequestFiles;
        }
      }
    });
  }

  refreshGrid() {
    this.vendorService.getVendorBatchRequest().subscribe({
      next: (data: AFVendorBatchRequestDomain) => {
        this.vendorBatchRequestData = data;
        if (this.vendorBatchRequestData.batchRequestFiles != null) {
          this.dataSource.data = this.vendorBatchRequestData.batchRequestFiles;
        }
      }
    });
  }

  get vendorAccount() { return this.vendorBatchRequestForm.get('vendorAccount') }

  createGroup() {
    this.vendorBatchRequestForm = this.fb.group({
      vendorAccount: ['', Validators.required],
      vendorFirstName: [''],
      vendorLastName: [''],
      VendorEmailAddress: ['']
    });
  }

  downloadAttachment(attachmentId: string, fileName: string): void {
    this.userService.downloadAttachment(attachmentId, fileName).subscribe();
  }     

  valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  }

  ascOrderVendorAccountFirst = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    try {
      if (a.value == this.vendorBatchRequestData.vendorAccount) {
        return -1;
      }
      if (b.value == this.vendorBatchRequestData.vendorAccount) {
        return 1;
      }
      return a.value.localeCompare(b.value);
    } catch (e) {
      console.log(e);
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onClickSubmit() {
    if (this.validate()) {
      this.uploadFiles();
    } else {
      this.eventBus.emit(new BeforeSubmitErrorEvent({}));
    }
  }

  getFileExtension = (name) => name.split('.').pop();
  getFileSizeInMb = (size) => size / (1024 * 1024);

  validate() {
    this.selected = (this.files.length > 0);
    let noValidationErrors = this.files.every(x => !x.validationErrors || x.validationErrors.length == 0);
    return this.selected && noValidationErrors && !this.vendorBatchRequestForm.invalid;
  }

  onClick() {
    const fileUpload = document.getElementById('fileUpload') as HTMLInputElement;
    fileUpload.onchange = () => {
      this.selected = true;
      for (const file of Array.from(fileUpload.files)) {
        this.files.splice(0, this.files.length);
        const fileModel = {
          data: file, state: 'in',
          inProgress: false, progress: 0, canRetry: false, canCancel: true, error: null
        };
        this.validateFile(fileModel);
        this.files.push(fileModel);
      }
      this.fileNameFocus();
    };

    fileUpload.click();
  }

  fileNameFocus(): void {
    setTimeout(() => {
      this.addedFile.nativeElement.focus();
    });
  }

  cancelFile(file: FileUploadModel) {
    if (file.sub) {
      file.sub.unsubscribe();
    }

    this.removeFileFromArray(file);
  }

  retryFile(file: FileUploadModel) {
    this.uploadFile(file);
    file.canRetry = false;
    file.error = null;
  }

  validateFile(file: FileUploadModel) {
    file.validationErrors = [];
    const fileExtension = this.getFileExtension(file.data.name);
    if (!this.supportedFileExtension.includes(fileExtension)) {
      file.validationErrors.push("Allowed file types are 'xls', 'xlsx'.");
    }

    if (this.getFileSizeInMb(file.data.size) > this.maxFileSupportedInMb) {
      file.validationErrors.push("File size is more than 5mb.");
    }
  }

  private uploadFile(file: FileUploadModel) {
    const fd = new FormData();
    fd.append(this.param, file.data);
    fd.append("verifyId", this.userService.getVerifyId());
    fd.append("noOfBatchRequests", String(this.vendorBatchRequestData.noOfBatchRequests));
    fd.append("vendorAccount", this.vendorBatchRequestData.vendorAccountSerialNum);

    let targetUrl: string = this.target;

    const req = new HttpRequest('POST', targetUrl, fd, {
      reportProgress: true
    });

    file.inProgress = true;

    file.sub = this._http.request(req).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            file.progress = Math.round(event.loaded * 100 / event.total);
            break;
          case HttpEventType.Response:
            return event;
        }
      }),
      tap(() => { }),
      last(),
      catchError((error: HttpErrorResponse) => {
        file.inProgress = false;
        file.canRetry = true;
        let err: string = error.error;
        if (err.includes('large')) {
          return of(`${file.data.name} : The attached file is too large to be processed, 500 records maximum. Please split the file into multiple files and upload.`);
        }
        return of(`${file.data.name} : ${error.error}`);
      })
    ).subscribe(
      (event: any) => {
        if (event instanceof Object) {
          this.eventBus.emit(new ShowProgressEvent({ show: false }));
          this.removeFileFromArray(file);
          this.refreshGrid();
          let vendor = event.body;
          let statusStr: string = '';
          if (vendor.responseCode) {
            statusStr = "<div style='padding-top:80px; padding-bottom:8px; height:30px;'>The background checks have been initiated successfully for the applicants in the attached file.</div>";
            if (vendor.errorMsg) {
              statusStr = statusStr + "<div style='padding-top:16px; padding-bottom:8px; height:30px;'>Couldn't create case for following email addresses.</div>";
              statusStr = statusStr + this.findEmailAddresses(vendor.errorMsg, statusStr);
            }
          } else if (vendor.errorMsg) {
            statusStr = vendor.errorMsg;
          }
          if (statusStr) {
            this.dialog.open(ErrorDialogComponent, {
              role: 'alertdialog',
              autoFocus: false,
              data: {
                dialogData: statusStr
              }
            });
          }
        } else {
          let config = new MatSnackBarConfig();
          let horizontalPosition: MatSnackBarHorizontalPosition = 'center';
          let verticalPosition: MatSnackBarVerticalPosition = 'top';
          config.verticalPosition = verticalPosition;
          config.horizontalPosition = horizontalPosition;
          config.duration = 5000;
          config.panelClass = 'submit-error';
          file.error = event;
          this._snackBar.open(event, "Close", config);
        }
      }
    );
  }

  findEmailAddresses(str: string, statusStr: string) {
    let email = " "; // if no match, use this
    if (!str) {
      return email;
    }

    let emailsArray = str.match(/[^@<\s]+@[^@\s>]+/g);
    if (emailsArray) {
      email = "";

      // Server DoS by Loop, prevention by adding MAX_ITERATION amount 
      if (emailsArray.length < 100) {
        for (const emailAddr of emailsArray) {
          email += "<div style='padding-top:16px; padding-bottom:8px; height:30px;'>" + emailAddr + "</div>";
        }
      }
    }
    return email;
  }

  getEmailAddressesInString() {
    this.files = [];
  }

  deleteClick() {
    const fileUpload = document.getElementById('fileUpload') as HTMLInputElement;
    this.files = [];

    if (fileUpload.files.length > 0) {
      fileUpload.value = "";
    }
  }

  private uploadFiles() {
    const fileUpload = document.getElementById('fileUpload') as HTMLInputElement;
    fileUpload.value = '';
    this.selected = (this.files.length > 0);

    this.files.forEach(file => {
      this.uploadFile(file);
    });
  }

  private removeFileFromArray(file: FileUploadModel) {
    const index = this.files.indexOf(file);
    if (index > -1) {
      this.files.splice(index, 1);
    }
  }

  downloadBatchRequest(id: number, fileName: string): void {
    this.vendorService.downloadBatchRequest(id, this.userService.getVerifyId(), fileName).subscribe();
  }
}

export class FileUploadModel {
  data: File;
  state: string;
  inProgress: boolean;
  progress: number;
  canRetry: boolean;
  canCancel: boolean;
  sub?: Subscription;
  error: string;
  validationErrors?: string[]
}



